import { useState } from "react";
import api from "@/lib/api";
import { flagFor } from "@/lib/constants";
import { Wallet, Plus, Trash2, Sparkles, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "GBP", "EUR", "CAD", "NGN", "AUD", "ZAR", "INR", "JPY"];

function AllocBar({ title, items, currency }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5">
      <p className="mb-3 text-sm font-semibold text-stone-900">{title}</p>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.name}>
            <div className="flex justify-between text-sm">
              <span className="text-stone-700">{it.name}</span>
              <span className="tabular text-stone-500">{it.pct}%</span>
            </div>
            <div className="mt-1 h-2 w-full rounded-full bg-stone-100">
              <div className="h-2 rounded-full bg-emerald-700 transition-all" style={{ width: `${it.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Portfolio() {
  const [rows, setRows] = useState([{ query: "", amount: "" }, { query: "", amount: "" }]);
  const [currency, setCurrency] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const setRow = (i, k, v) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((p) => [...p, { query: "", amount: "" }]);
  const delRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

  const analyze = async () => {
    const holdings = rows.filter((r) => r.query && parseFloat(r.amount) > 0)
      .map((r) => ({ query: r.query, amount: parseFloat(r.amount) }));
    if (!holdings.length) { toast.error("Add at least one holding with an amount."); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/portfolio/analyze", { holdings, base_currency: currency });
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed."); }
    finally { setLoading(false); }
  };

  const fmt = (n) => `${currency} ${Number(n || 0).toLocaleString()}`;
  const score = res?.ai?.diversification_score ?? 0;

  return (
    <main className="mx-auto max-w-5xl px-6 py-10 md:px-8">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500"><Wallet size={14} strokeWidth={1.5} /> Portfolio Analyzer</p>
      <h1 className="mt-2 font-head text-3xl font-medium tracking-tight text-stone-900 sm:text-4xl">Analyze your portfolio</h1>
      <p className="mt-2 max-w-2xl text-stone-600">Add your holdings and invested amounts. We'll break down your diversification across regions, sectors and currencies, then flag concentration risks.</p>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-stone-900">Your holdings</p>
          <select data-testid="portfolio-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}
            className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20">
            {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-3">
              <input data-testid={`holding-query-${i}`} value={r.query} onChange={(e) => setRow(i, "query", e.target.value)}
                placeholder="Company or ticker (e.g. Apple, MTN Nigeria, SHOP.TO)"
                className="h-11 flex-1 rounded-lg border border-stone-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20" />
              <input data-testid={`holding-amount-${i}`} type="number" min="0" value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)}
                placeholder="Amount"
                className="h-11 w-32 rounded-lg border border-stone-200 bg-white px-3 text-sm tabular focus:outline-none focus:ring-2 focus:ring-navy/20" />
              <button data-testid={`holding-remove-${i}`} onClick={() => delRow(i)} disabled={rows.length <= 1}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-500 transition-colors hover:bg-stone-50 hover:text-rose-600 disabled:opacity-40">
                <Trash2 size={16} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="add-holding-btn" onClick={addRow}
            className="flex h-11 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50">
            <Plus size={16} strokeWidth={1.5} /> Add holding
          </button>
          <button data-testid="analyze-portfolio-btn" disabled={loading} onClick={analyze}
            className="flex h-11 items-center gap-2 rounded-lg bg-navy px-5 text-sm font-medium text-white transition-colors hover:bg-navyd disabled:opacity-60">
            <Sparkles size={16} strokeWidth={1.5} /> {loading ? "Analyzing…" : "Analyze portfolio"}
          </button>
        </div>
      </div>

      {res && !loading && (
        <div className="mt-6 space-y-5 animate-in-up" data-testid="portfolio-report">
          {res.warning && (
            <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="intl-warning">
              <TriangleAlert size={18} className="mt-0.5 shrink-0 text-amber-700" strokeWidth={1.5} />
              <p className="text-sm text-amber-800">{res.warning}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-xl border border-stone-200 bg-white p-5">
              <p className="text-sm text-stone-500">Portfolio score</p>
              <p className="text-2xl font-semibold text-navy" data-testid="portfolio-score">{res.ai?.portfolio_score ?? "—"}/100</p>
              <div className="mt-2 h-2 w-full rounded-full bg-stone-100"><div className="h-2 rounded-full bg-emerald-700" style={{ width: `${res.ai?.portfolio_score || 0}%` }} /></div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-5">
              <p className="text-sm text-stone-500">Total value</p>
              <p className="tabular text-2xl font-semibold text-stone-900">{fmt(res.total_value)}</p>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-5">
              <p className="text-sm text-stone-500">Diversification</p>
              <p className="text-2xl font-semibold text-navy">{score}/100 <span className="text-base font-medium text-stone-500">{res.ai?.diversification_label}</span></p>
              <div className="mt-2 h-2 w-full rounded-full bg-stone-100"><div className="h-2 rounded-full bg-emerald-700" style={{ width: `${score}%` }} /></div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-5">
              <p className="text-sm text-stone-500">Risk level</p>
              <p className="text-2xl font-semibold text-stone-900">{res.ai?.risk_level}</p>
            </div>
          </div>

          {res.ai?.estimated_dividend_income && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <p className="text-sm"><span className="font-semibold text-navy">Estimated dividend income: </span><span className="text-stone-700">{res.ai.estimated_dividend_income}</span></p>
            </div>
          )}

          <div className="rounded-xl border border-stone-200 bg-white p-6">
            <p className="mb-3 text-sm font-semibold text-stone-900">Holdings</p>
            <div className="divide-y divide-stone-100">
              {res.holdings.map((h) => (
                <div key={h.ticker} className="flex items-center justify-between py-2.5" data-testid={`portfolio-holding-${h.ticker}`}>
                  <span className="text-sm text-stone-800">{flagFor(h.country)} {h.name} <span className="text-stone-400">· {h.sector}</span></span>
                  <span className="tabular text-sm font-medium text-stone-700">{fmt(h.amount)} · {h.weight}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <AllocBar title="By region" items={res.region_allocation} currency={currency} />
            <AllocBar title="By sector" items={res.sector_allocation} currency={currency} />
            <AllocBar title="By currency" items={res.currency_allocation} currency={currency} />
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-6">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-navy"><Sparkles size={14} /> AI Analysis</p>
            <p className="mt-3 text-stone-700">{res.ai?.summary}</p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4">
                <p className="text-sm font-semibold text-rose-700">Concentration risks</p>
                <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-stone-700">{(res.ai?.concentration_risks || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                <p className="text-sm font-semibold text-navy">Strengths</p>
                <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-stone-700">{(res.ai?.strengths || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            </div>
            <div className="mt-4 rounded-lg bg-stone-900 p-4 text-sm text-stone-100">
              <p className="font-semibold text-emerald-300">Suggestions</p>
              <ul className="mt-2 list-disc space-y-1 pl-4">{(res.ai?.suggestions || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          </div>
          <p className="text-center text-xs text-stone-400">{res.disclaimer}</p>
        </div>
      )}
    </main>
  );
}
